/**
 * The pure half of viewport-driven thumbnail warming: which deck to talk to
 * next, and when. No React, no fetch — so it is unit-testable, which matters
 * because every bug this thing can have looks like "nothing happened".
 *
 * The shape follows the server's constraints, not the UI's:
 *
 * - **One deck at a time.** `inFlight` on the server is per slug, and rendering
 *   spawns that deck's Vite child out of a pool only four wide (shared with the
 *   workbench child that serves the gallery). Two decks at once is how you evict
 *   the thing you are looking at.
 * - **The wanted set SHRINKS.** This is the real difference from the dashboard's
 *   cover queue, where a deck once wanted is wanted forever. Here the user scrolls
 *   away and those slides stop mattering: the whole point is not to render them.
 * - **`busy` means wait, not fail.** The server reports `busy` when a pass of a
 *   different kind (a whole-deck warm, a cover) owns the slug. Unlike the cover
 *   case — where that pass is not making the image you want — a whole-deck pass
 *   renders exactly the slides you are asking for, so the right answer is to step
 *   aside so other decks can proceed and ask again shortly. Never blackball.
 * - **The server carries the terminal weight.** It remembers per-slide failures
 *   (`givenUp`) and deck-wide backoff (`coolingUntil`), so this queue does not
 *   have to decide when a slide is hopeless — a question it cannot answer anyway,
 *   because a user who keeps scrolling keeps resetting any client-side stall clock.
 */

// How long a deck waits after reporting `busy` before it is asked again.
export const DEFER_MS = 4_000;

// How many times a deck may be told `busy` BECAUSE THE GLOBAL PASS SLOT IS
// HELD BY ANOTHER DECK before we stop asking. That busy is unproductive — no
// image for this deck is being made — and with no cap a deck starved behind one
// slow deck simply waited forever, which is the "so many hang" the viewport
// switch made common (the dashboard's serial cover queue holding the single
// pass slot while an open overview's tiles wait). At DEFER_MS apart this is
// ~48s of someone else's pass. We do NOT cap the same-deck case: a `'deck'`
// busy means OUR own whole-deck pass is rendering these slides, so waiting is
// productive and that pass has its own deadline (and is swept if wedged).
export const MAX_DEFERS = 12;

// Give up on the CURRENT batch when nothing has arrived for this long. Generous:
// a cold deck's first capture waits on its Vite child compiling the whole module
// graph, which the server allows 45s for.
export const NO_PROGRESS_MS = 75_000;

/** One deck's batch: the slides it wants, and when it may next be asked. */
export type WarmJob = {
  slug: string;
  ids: string[];
  /** Epoch ms before which this deck must not be promoted (see DEFER_MS). */
  notBefore: number;
  defers: number;
};

export type WarmState = {
  queue: WarmJob[];
  /** The one deck currently being asked — the runner is serial by design. */
  active: WarmJob | null;
  /** Batches dropped after MAX_DEFERS of capacity-busy, for the surface to
   *  offer as retryable rather than leaving them grey forever. */
  starved: { slug: string; ids: string[] }[];
};

export type WarmAction =
  /** The viewport moved: replace what each deck wants. A deck absent from
   *  `bySlug` is off screen. */
  | { type: 'visible'; bySlug: Record<string, string[]>; now?: number }
  /** Promote the first deck that is due. */
  | { type: 'start'; now: number }
  /** Step aside for someone else's pass. `kind: 'deck'` (our own whole-deck
   *  pass) is productive and never capped; 'capacity' is capped at MAX_DEFERS. */
  | { type: 'busy'; slug: string; now: number; kind?: 'deck' | 'capacity' }
  /** The surface absorbed `starved`; forget it so it does not re-fire. */
  | { type: 'starved-consumed' }
  /** This deck's batch is done (landed, or nothing was queued for it). */
  | { type: 'settle'; slug: string }
  /** The surface closed or was disabled. */
  | { type: 'clear' };

export const INITIAL: WarmState = { queue: [], active: null, starved: [] };

const at = (state: WarmState, slug: string): number => state.queue.findIndex((j) => j.slug === slug);

/**
 * The queue's one transition function. Every case is documented on `WarmAction`
 * rather than here, so the union and its prose cannot drift.
 *
 * @param state
 *   - `{type:'visible', bySlug: Record<slug, string[]>, now}` the viewport moved:
 *     replace what each deck wants. A deck absent from `bySlug` is off screen.
 *   - `{type:'start', now}` promote the first deck that is due.
 *   - `{type:'busy', slug, now, kind?: 'deck'|'capacity'}` step aside for someone
 *     else's pass. `kind` 'deck' (our own whole-deck pass) is productive and is
 *     never capped; 'capacity' (another deck holds the global slot) is capped at
 *     MAX_DEFERS and past it moved to `starved`, so the surface can show a
 *     retry instead of waiting forever.
 *   - `{type:'starved-consumed'}` the surface absorbed `starved`; forget it so it
 *     does not re-fire.
 *   - `{type:'settle', slug}` this deck's batch is done (landed, or nothing was
 *     queued for it — a cached, off-plan or given-up id is not worth waiting on).
 *   - `{type:'clear'}` the surface closed or was disabled.
 */
export function reducer(state: WarmState, action: WarmAction): WarmState {
  switch (action.type) {
    case 'visible': {
      const { bySlug } = action;
      const queue: WarmJob[] = [];
      for (const [slug, ids] of Object.entries(bySlug)) {
        if (!ids.length) continue;
        // Keep a deck's place (and its defer timer) if it was already queued —
        // re-queueing on every scroll tick would reset `notBefore` and let a
        // busy deck monopolise the runner.
        const i = at(state, slug);
        const existing = i === -1 ? undefined : state.queue[i];
        queue.push(existing ? { ...existing, ids } : { slug, ids, notBefore: 0, defers: 0 });
      }
      // Retire the running job once nothing it is waiting for is on screen any
      // more. The runner is SERIAL, so an active job that has scrolled away
      // blocks every slide that is now in front of the user — scroll down and
      // straight back up and the tiles you are looking at wait behind a batch
      // for tiles you are not, until that batch's own stall budget expires. That
      // reads as the grid hanging, and it is why this is not "leave it alone".
      //
      // Nothing is lost by letting go. The server pass cannot be called back and
      // carries on regardless; its images land in the cache and the next poll
      // absorbs them, because `thumbs` comes back whole-deck rather than
      // per-batch.
      const active = state.active;
      const stillWanted = active && (bySlug[active.slug] || []).some((id) => active.ids.includes(id));
      return stillWanted ? { ...state, queue } : { ...state, queue, active: null };
    }
    case 'start': {
      if (state.active || state.queue.length === 0) return state;
      const i = state.queue.findIndex((j) => j.notBefore <= action.now);
      if (i === -1) return state; // everything is deferred; the runner waits
      const queue = state.queue.slice();
      const [next] = queue.splice(i, 1);
      // splice always yields one element for an index it found, but the type
      // does not know that; a missing job is the same as nothing being due.
      if (!next) return state;
      return { ...state, queue, active: next };
    }
    case 'busy': {
      if (state.active?.slug !== action.slug) return state;
      const job = { ...state.active, notBefore: action.now + DEFER_MS, defers: state.active.defers + 1 };
      // Same-deck busy ('deck'): OUR whole-deck pass is running and rendering the
      // exact slides we want, so waiting is productive — never cap it (that pass
      // has its own PASS_TIMEOUT_MS deadline, and a wedged one is swept).
      // Capacity busy(someone else holds the global slot): no image we want is
      // being made, and re-queuing forever behind it is the "grid hangs" this
      // queue must not replicate. Past MAX_DEFERS, drop the deck and report the
      // ids so the surface can offer a retry instead of a permanent grey wait.
      // Nothing is lost by letting go: the server pass carries on regardless and
      // a later poll absorbs whatever it lands.
      if (action.kind !== 'deck' && job.defers > MAX_DEFERS) {
        return {
          ...state,
          active: null,
          queue: state.queue,
          starved: [...state.starved, { slug: action.slug, ids: job.ids }],
        };
      }
      // Back of the queue, so several open shelves take turns instead of the
      // first one holding the runner while a whole-deck pass grinds.
      return { ...state, active: null, queue: [...state.queue, job] };
    }
    case 'starved-consumed':
      return { ...state, starved: [] };
    case 'settle': {
      if (state.active?.slug !== action.slug) return state;
      // Not remembered as done: the user can scroll back, and by then the deck's
      // state has usually changed (its images landed). A fresh `visible` decides.
      return { ...state, active: null };
    }
    case 'clear':
      return { ...state, queue: [], starved: [] };
    default:
      return state;
  }
}

/**
 * Ids from a batch that are still worth waiting for: not yet arrived, and not
 * ones the server has stopped trying to render.
 *
 * This is what a poll checks, rather than the deck-wide `pending` count. That
 * count no longer drains monotonically once most slides are deliberately never
 * requested, so "the count stopped moving" cannot tell "nothing is progressing"
 * from "nothing was asked for".
 */
export function outstanding(
  batch: string[] | null | undefined,
  thumbs: Record<string, unknown> | null | undefined,
  givenUp: string[] | null | undefined,
): string[] {
  const gone = new Set(givenUp || []);
  return (batch || []).filter((id) => !thumbs?.[id] && !gone.has(id));
}
